import React, { useEffect, useId, useRef, useState } from "react";
import { CircleHelp, MessageCircleQuestion, Play, Search, X } from "lucide-react";
import { listFaqs } from "../../apiFaq";
import "./FaqPopover.css";

const highlightQuestion = (text, search) => {
  if (!search) return text;
  const parts = [];
  let start = 0;
  let match;
  while ((match = text.toLowerCase().indexOf(search.toLowerCase(), start)) !== -1) {
    parts.push(text.slice(start, match), <mark key={match}>{text.slice(match, match + search.length)}</mark>);
    start = match + search.length;
  }
  parts.push(text.slice(start));
  return parts;
};

export default function FaqPopover({ sourceDbConfigId, databaseName, disabled, refreshKey, onRun }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [items, setItems] = useState([]);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const dialogId = useId();

  useEffect(() => {
    setItems([]);
    setSearch("");
    if (!sourceDbConfigId) { setOpen(false); return; }
    if (!open) return;
    setLoading(true);
    setError("");
    const controller = new AbortController();
    listFaqs(sourceDbConfigId, { signal: controller.signal }).then(items => {
      if (!controller.signal.aborted) { setItems(items); setLoading(false); }
    }).catch(error => {
      if (!controller.signal.aborted) {
        setError(error.response?.data?.error || "Unable to load example questions.");
        setLoading(false);
      }
    });
    return () => controller.abort();
  }, [open, sourceDbConfigId, refreshKey, retry]);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onPointerDown = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  const term = search.trim();
  const filteredItems = items.filter(item => [item.question_text, ...item.tags]
    .some(text => text.toLowerCase().includes(term.toLowerCase())));

  return <div className="faq-anchor" ref={rootRef}>
    <button className="faq-fab" ref={triggerRef} aria-label="FAQs" title="FAQs"
      aria-expanded={open} aria-controls={dialogId} aria-haspopup="dialog"
      disabled={!sourceDbConfigId} onClick={() => setOpen(value => !value)}>
      <CircleHelp size={25} />
    </button>
    {open && <section id={dialogId} role="dialog" aria-label="FAQs" className="faq-popover">
      <header className="faq-header">
        <MessageCircleQuestion size={30} aria-hidden="true" />
        <div><h2>FAQs</h2><p>Example questions · {databaseName}</p></div>
        <button aria-label="Close FAQs" className="faq-icon-button" onClick={() => {
          setOpen(false);
          triggerRef.current?.focus();
        }}><X size={18} /></button>
      </header>
      <div className="faq-search">
        <Search size={18} aria-hidden="true" />
        <input ref={searchRef} type="search" aria-label="Search FAQs" placeholder="Search questions…"
          value={search} onChange={event => setSearch(event.target.value)} />
        {search && <button aria-label="Clear search" className="faq-icon-button" onClick={() => {
          setSearch(""); searchRef.current?.focus();
        }}><X size={16} /></button>}
      </div>
      <div className="faq-list" aria-busy={loading}>
        {loading && <p className="faq-state" role="status">Loading questions…</p>}
        {error && <div className="faq-state" role="alert">{error}<button className="faq-retry" onClick={() => setRetry(value => value + 1)}>Retry</button></div>}
        {!loading && !error && items.length === 0 && <p className="faq-state">No example questions available for this database.</p>}
        {!loading && !error && items.length > 0 && filteredItems.length === 0 && <p className="faq-state">No matching questions.</p>}
        {filteredItems.map(item => <button className="faq-question" key={item.id} aria-label={item.question_text}
          disabled={disabled || loading || Boolean(error)} onClick={() => onRun(item)}>
          <span>{highlightQuestion(item.question_text, term)}</span>
          <span className="faq-play" aria-hidden="true"><Play size={14} fill="currentColor" /></span>
        </button>)}
      </div>
      <footer>Click a question to run it</footer>
    </section>}
  </div>;
}
